import { Col, Radio, Row } from 'antd';
import { useGetFilter } from 'hooks/useSearchParamsObj';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicObjectType } from 'types/commons';
import ChannelFilter from '../components/Filters/ChannelFilter';
import { TFilterItem } from 'components/rest/Filter/FilterRow';
import { sumBy, toArray, upperFirst } from 'lodash';
import {
  CHANNELS_FILTER,
  DELIVERY_PARTNER_FILTER,
  DELIVERY_TYPES,
  IS_VOUCHER,
  ORDER_TYPE,
  PAYMENT_STATUSES,
  SELLER_ORDER_STATUS_CONST,
  SHIPPING_METHOD,
} from 'constants/constants';
import FilterComponent from '../components/Filters/FilterComponent';
import { useDispatch, useSelector } from '@redux';
import { getOrderStatusCount } from '@redux/config/actions';
import {
  DATA_FIELD_SEARCH,
  DATA_PICK_PACK_FIELD_SEARCH,
  SEARCH_VALUE,
} from './utils';
import { OPERATORS_WITH_CONST } from 'constants/filters';
import crudActions from '@redux/crudActions';
import RestList from 'containers/rest/List';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import { TLayoutItem } from '@redux/layouts/actions';
import { getColumns } from './columns';
import { getListProps } from './list';
import ButtonCreateBatch from '../components/ButtonCreateBatch';
import useGetSearchKey from 'hooks/useSearchKeys';
import { ORDER_STATUS } from 'constants/orders';
import { OrderListWrapper } from './styles';
import { newCheckRole } from 'utils/tools';

interface IOrderListProps {
  customerData?: DynamicObjectType;
  isShowFixed?: boolean;
}

const List = React.memo(({ customerData, isShowFixed }: IOrderListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const listReft = useRef(null);

  const { searchKeys: ordersSearchKeys } = useGetSearchKey(DATA_FIELD_SEARCH);
  const { searchKeys: pickPackSearchKeys } = useGetSearchKey(
    DATA_PICK_PACK_FIELD_SEARCH,
  );

  const statusCount = useSelector<DynamicObjectType[]>(
    (state) => state.config.statusCount,
  );
  const user = useSelector<DynamicObjectType>((state) => state.auth.data);
  const { searchParams, setSearchParams, filter } = useGetFilter();

  useEffect(() => {
    dispatch(crudActions['orderBatches'].clearCurrent());
    dispatch(crudActions['orders'].clearCurrent());
    dispatch(crudActions['orderHistories'].resetData());
  }, [dispatch]);

  const handleChangeRadioTabs = useCallback(
    (e) => {
      searchParams.set('statusOms', e?.target?.value);
      searchParams.delete('page');
      searchParams.delete('pageSize');
      searchParams.delete('orderBy');
      searchParams.delete('filters');
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const filters = useMemo(() => {
    const _filters: TFilterItem[] = [
      {
        key: ['PICKING', 'PACKING'].includes(searchParams?.get('statusOms'))
          ? 'orders.statusOms'
          : 'statusOms',
        label: 'Order Status',
        dataSource: toArray(SELLER_ORDER_STATUS_CONST).map(
          (e: DynamicObjectType) => ({
            value: e.value,
            label: t(e.text),
          }),
        ),
      },
      {
        key: 'voucher',
        label: 'Order with Voucher',
        dataSource: IS_VOUCHER,
      },
      {
        key: ['PICKING', 'PACKING'].includes(searchParams?.get('statusOms'))
          ? 'orders.paymentStatus'
          : 'paymentStatus',
        label: 'Payment Status',
        dataSource: PAYMENT_STATUSES.map((e: DynamicObjectType) => ({
          value: e.value,
          label: t(e.text),
        })),
      },
      {
        key: ['PICKING', 'PACKING'].includes(searchParams?.get('statusOms'))
          ? 'orders.paymentType'
          : 'paymentType',
        label: 'Payment Method',
        valueCPN: (
          <FilterComponent
            reference={{
              model: 'payment-method',
              valueProp: 'name',
              labelFormat: (e) => upperFirst(e?.name || ''),
              initFilters: {
                filters: [
                  {
                    condition: 'and',
                    key: 'isActive',
                    operator: 'eq',
                    value: true,
                  },
                ],
              },
            }}
          />
        ),
      },
      {
        key: 'createdAt',
        label: 'Order created time',
        isDatePicker: true,
      },
      {
        key: 'cancelTime',
        label: 'Order cancelled time',
        isDatePicker: true,
      },
      {
        key: ['PICKING', 'PACKING'].includes(searchParams?.get('statusOms'))
          ? 'orders.orderType'
          : 'orderType',
        label: 'Order Type',
        dataSource: ORDER_TYPE.map((e: DynamicObjectType) => ({
          value: e.value,
          label: t(e.text),
        })),
      },
      {
        key: 'channelDisplay',
        label: 'Channel',
        dataSource: CHANNELS_FILTER.map((e: DynamicObjectType) => ({
          value: e.value,
          label: t(e.label),
        })),
      },
      {
        key: ['PICKING', 'PACKING'].includes(searchParams?.get('statusOms'))
          ? 'orders.deliveryType'
          : 'deliveryType',
        label: 'Delivery type',
        dataSource: DELIVERY_TYPES.map((e: DynamicObjectType) => ({
          value: e.key,
          label: t(e.text),
        })),
      },
      {
        key: ['PICKING', 'PACKING'].includes(searchParams?.get('statusOms'))
          ? 'orders.shippingType'
          : 'shippingType',
        label: 'Delivery method',
        dataSource: SHIPPING_METHOD.map((e: DynamicObjectType) => ({
          value: e.key,
          label: t(e.text),
        })),
      },
      {
        key: ['PICKING', 'PACKING'].includes(searchParams?.get('statusOms'))
          ? 'shippingCarrier.name'
          : 'shippingCarrier.name',
        label: 'Delivery partner',
        dataSource: DELIVERY_PARTNER_FILTER,
        operators: OPERATORS_WITH_CONST,
      },
      {
        key: 'isGiftCards',
        label: 'Gift Cards',
        dataSource: IS_VOUCHER,
      },
      searchParams.get('statusOms') !== 'READY_TO_PICK'
        ? {
            key: ['PICKING', 'PACKING'].includes(searchParams?.get('statusOms'))
              ? 'orders.locationId'
              : 'locationId',
            label: 'Warehouse',
            valueCPN: (
              <FilterComponent
                reference={{
                  labelProp: 'name',
                  model: 'locations',
                  searchMoreKeys: ['id', 'name'],
                  valueProp: 'id',
                  labelFormat: (item) =>
                    item?.name ? `${item?.name} | ${item?.code}` : item?.code,
                  initFilters: {
                    filters: [
                      {
                        condition: 'and',
                        key: 'isOrderProcess',
                        operator: 'eq',
                        value: true,
                      },
                    ],
                  },
                }}
              />
            ),
          }
        : null,
      {
        key: 'note',
        label: 'Note',
        isCheckNull: true,
        dataSource: [
          {
            value: true,
            label: 'True',
          },
          {
            value: false,
            label: 'False',
          },
        ],
      },
      {
        key: 'isTo',
        label: 'Is TO',
        dataSource: IS_VOUCHER,
      },
      {
        key: 'deliveryCode',
        label: 'Shipping no',
        isCheckNull: true,
        dataSource: [
          {
            value: true,
            label: 'True',
          },
          {
            value: false,
            label: 'False',
          },
        ],
      },
      {
        key: 'newOrderExchangeId',
        label: 'Is Exchange',
        isCheckNull: true,
        dataSource: [
          {
            value: true,
            label: 'True',
          },
          {
            value: false,
            label: 'False',
          },
        ],
      },
    ];

    if (searchParams.get('status')) {
      return _filters?.filter(
        (item) =>
          !['orders.statusOms', 'statusOms', 'cancelTime']?.includes(item?.key),
      );
    }
    return _filters?.filter((item) => !!item?.key);
  }, [searchParams, t]);

  const hasChannelFilter = useMemo(
    () =>
      ['AWAITING_CONFIRMATION', 'CONFIRMED', 'READY_TO_PICK'].includes(
        searchParams.get('statusOms'),
      ),
    [searchParams.get('statusOms')],
  );

  const handleFilterChannel = useCallback(
    (value) => {
      searchParams.delete('page');
      searchParams.delete('pageSize');
      searchParams.set(
        'filters',
        JSON.stringify(
          [
            {
              ...(!value?.carrierId?.isnull
                ? value?.carrierId
                  ? {
                      key: 'shippingCarrierId',
                      value: value?.carrierId,
                      condition: 'and',
                      operator: 'eq',
                    }
                  : {}
                : {
                    condition: 'and',
                    key: 'shippingCarrierId',
                    operator: 'isnull',
                    value: true,
                  }),
            },
            {
              ...(value?.chainStoreId
                ? {
                    condition: 'and',
                    key: 'chainStoreId',
                    operator: 'eq',
                    value: value?.chainStoreId,
                  }
                : {}),
            },
            {
              ...(value?.channel
                ? {
                    condition: 'and',
                    key: 'channelDisplay',
                    operator: 'eq',
                    value: value?.channel,
                  }
                : {}),
            },
            {
              ...(value?.shippingType
                ? {
                    condition: 'and',
                    key: 'shippingType',
                    operator: 'eq',
                    value: value?.shippingType,
                  }
                : {}),
            },
            {
              ...(value?.orderType
                ? {
                    condition: 'and',
                    key: 'orderType',
                    operator: 'eq',
                    value: value?.orderType,
                  }
                : {
                    condition: 'and',
                    key: 'orderType',
                    operator: 'eq',
                    value: 'DELIVERY',
                  }),
            },
          ],
          null,
          0,
        ),
      );
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    dispatch(getOrderStatusCount({}));
  }, [searchParams.get('customLayout'), searchParams.get('statusOms')]);

  const handleNameTab = useCallback(
    (e) => {
      const data = ORDER_STATUS[e];

      if (data?.value !== 'all') {
        return (
          <div className="flex items-center">
            <div>{t(data?.label)}</div>
            <div className="rounded-[5px] px-1 py-0.5 ml-1 text-center min-w-10 count text-xs">{` ${
              sumBy(
                statusCount?.filter((item: DynamicObjectType) =>
                  data?.subValue?.includes(item?.statusOms),
                ),
                (o) => Number(o?.count),
              ) || 0
            }`}</div>
          </div>
        );
      }
      return t(data?.label || e);
    },
    [statusCount, t],
  );

  const INITIAL_LAYOUTS: TLayoutItem[] = useMemo(
    () => [
      {
        rootPath: '/',
        pathId: 'CONFIRMATION',
        title: 'Confirmation',
        label: handleNameTab('CONFIRMATION'),
        customPath: `/?customLayout=CONFIRMATION&statusOms=AWAITING_CONFIRMATION`,
        isDefault: true,
      },
      {
        rootPath: '/',
        pathId: 'PROCESSING',
        title: 'Processing',
        label: handleNameTab('PROCESSING'),
        customPath: `/?customLayout=PROCESSING&statusOms=READY_TO_PICK`,
        isDefault: true,
      },
      {
        rootPath: '/',
        pathId: 'DELIVERING',
        title: 'Delivering',
        label: handleNameTab('DELIVERING'),
        customPath: `/?customLayout=DELIVERING&statusOms=DELIVERING`,
        isDefault: true,
      },
      {
        rootPath: '/',
        pathId: 'FAIL_QC',
        title: 'Fail QC',
        label: handleNameTab('FAIL_QC'),
        customPath: `/?customLayout=FAIL_QC&statusOms=FAIL_QC`,
        isDefault: true,
      },
      {
        rootPath: '/',
        pathId: 'PENDING',
        title: 'Pending',
        label: handleNameTab('PENDING'),
        customPath: `/?customLayout=PENDING&statusOms=MANUALLY_PENDING`,
        isDefault: true,
      },
    ],
    [handleNameTab],
  );

  const _columns = useMemo(() => {
    return getColumns(
      searchParams.get('statusOms') || searchParams.get('customLayout'),
      user?.roles,
      user?.isSuperUser,
    ) as IRestColumnProps[];
  }, [searchParams, user]);

  const _listProps = useMemo(() => {
    return getListProps(
      searchParams.get('statusOms'),
      searchParams,
      filter,
      {
        condition: 'and',
        key: 'customerId',
        operator: 'is',
        value: customerData?.id,
      },
      user?.roles,
      user?.isSuperUser,
    );
  }, [searchParams, filter, customerData?.id, user]);

  const _searchKeys = useCallback(() => {
    return searchParams.get('statusOms') === 'PICKING' ||
      searchParams.get('statusOms') === 'PACKING'
      ? {
          searchKeys: pickPackSearchKeys,
          dataFieldSearch: DATA_PICK_PACK_FIELD_SEARCH,
        }
      : {
          searchKeys: ordersSearchKeys,
          dataFieldSearch: DATA_FIELD_SEARCH,
        };
  }, [searchParams, pickPackSearchKeys, ordersSearchKeys]);

  const hasSubCustomLayout = useMemo(() => {
    if (!searchParams.get('customLayout')) return null;

    return (
      <Radio.Group
        buttonStyle="solid"
        size="large"
        onChange={handleChangeRadioTabs}
        value={searchParams.get('statusOms')}
        className="mt-5"
      >
        {SEARCH_VALUE[searchParams.get('customLayout') || 'all']
          ?.filter((item: DynamicObjectType) =>
            item?.endpoint
              ? newCheckRole(
                  item?.endpoint,
                  'GET',
                  user?.roles,
                  user?.isSuperUser,
                )
              : true,
          )
          .map((item, idx) => (
            <Radio.Button key={String(idx)} value={item.value}>
              <div className="flex items-center">
                {t(item.text)}
                <div className="rounded-[5px] px-1 py-0.5 ml-1 text-center min-w-10 count text-xs">
                  {statusCount?.find(
                    (status) =>
                      status.statusOms === item.value ||
                      status.statusOms === item.countKey,
                  )?.count || 0}
                </div>
              </div>
            </Radio.Button>
          ))}
      </Radio.Group>
    );
  }, [handleChangeRadioTabs, searchParams, statusCount, t, user]);

  return (
    <OrderListWrapper>
      <Row gutter={40}>
        {hasChannelFilter && (
          <ChannelFilter
            status={searchParams.get('statusOms')}
            onFilter={handleFilterChannel}
            isShowFixed={isShowFixed}
          />
        )}
        <Col {...(!hasChannelFilter ? { span: 24 } : { xs: 24, md: 19 })}>
          <RestList
            columns={_columns}
            filters={filters}
            searchKeys={_searchKeys().searchKeys}
            model="orders"
            placeholderInputSearch="Search by Order ID, SO, SKU, Shipping Phone, Email, ..."
            dataFieldSearch={_searchKeys().dataFieldSearch}
            hasCustomLayout
            initialLayouts={INITIAL_LAYOUTS}
            {..._listProps}
            hasCardComponent={
              searchParams.get('statusOms') === 'READY_TO_PICK' &&
              newCheckRole(
                '/order-batches/create/manual',
                'POST',
                user?.roles,
                user?.isSuperUser,
              ) && <ButtonCreateBatch listReft={listReft} />
            }
            listReft={listReft}
            hasSubCustomLayout={hasSubCustomLayout}
          />
        </Col>
      </Row>
    </OrderListWrapper>
  );
});

export default List;
